import type { LocalDate } from "@js-joda/core";
import { Api } from "services/api/api.service";
import type { BackendAxiosError } from "services/api/backendError";
import type { StaffProfileData } from "services/api/company/staff.service";
import type {
  EmploymentDetailsForm,
  EmploymentSaveMode,
} from "../profilePanel.contract";
import { resolveStaffProfilePanelErrorMessage } from "../domains/employment/profilePanel.error.logic";
import {
  buildCreateWorkerEmploymentPayload,
  buildUpdateStaffWorkerPayload,
} from "../domains/employment/profilePanel.payload.logic";
import type { toEmploymentUiRules } from "../domains/employment/profilePanel.rules.logic";

interface PerformSaveEmploymentParams {
  companyId: number | null;
  workerId: number | null;
  profile: StaffProfileData | null;
  editedEmploymentDetails: EmploymentDetailsForm;
  employmentSaveMode: EmploymentSaveMode;
  editingTab: "employmentSalary" | "profile" | null;
  employmentRules: ReturnType<typeof toEmploymentUiRules>;
  startDate: LocalDate | null;
  endDate: LocalDate | null;
  translate: (key: string, options?: Record<string, unknown>) => string;
  setIsSaving: (value: boolean) => void;
  setSaveError: (value: string) => void;
  setEditingTab: (value: null) => void;
  setEmploymentSaveMode: (value: null) => void;
  setModalState: (value: { type: "none" }) => void;
  reloadProfile: () => void;
  onWorkerUpdated?: () => void;
}

export const performSaveEmployment = (
  params: PerformSaveEmploymentParams,
): void => {
  if (params.editingTab !== "employmentSalary") return;
  if (params.companyId == null || params.workerId == null) return;
  if (params.employmentSaveMode == null) return;

  if (!params.startDate) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return;
  }

  if (params.employmentRules.isEndDateRequiredOnSave && !params.endDate) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return;
  }

  const currentEmployment = params.profile?.employmentInfo?.current;
  if (params.employmentSaveMode === "update" && currentEmployment?.id == null) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return;
  }

  const payloadParams = {
    probationPeriodEndDate: currentEmployment?.probationPeriodEndDate ?? null,
    editedEmploymentDetails: params.editedEmploymentDetails,
    effectiveFromDate: params.startDate,
    effectiveEndDate: params.endDate,
  };
  const companyId = params.companyId;
  const workerId = params.workerId;

  params.setIsSaving(true);
  params.setSaveError("");

  const savePromise =
    params.employmentSaveMode === "create"
      ? Api().company.staff.createWorkerEmployment(
          companyId,
          workerId,
          buildCreateWorkerEmploymentPayload(payloadParams).payload,
        )
      : (() => {
          if (currentEmployment == null || currentEmployment.id == null) {
            return Promise.reject(new Error("missing-employment-id"));
          }

          return Api().company.staff.updateWorkerEmployment(
            companyId,
            workerId,
            currentEmployment.id,
            buildUpdateStaffWorkerPayload(payloadParams).payload,
          );
        })();

  savePromise
    .then(() => {
      params.setEditingTab(null);
      params.setEmploymentSaveMode(null);
      params.setModalState({ type: "none" });
      params.reloadProfile();
      params.onWorkerUpdated?.();
    })
    .catch((error: BackendAxiosError) => {
      params.setSaveError(
        resolveStaffProfilePanelErrorMessage(error, params.translate),
      );
    })
    .finally(() => {
      params.setIsSaving(false);
    });
};
